import { useMemo } from "react";
import { TrendingUp, TrendingDown, CalendarDays, Users } from "lucide-react";
import { buildBookingForecast, type ForecastInputRow } from "@/lib/booking-forecast";

/**
 * Forward-looking booking forecast for the admin dashboard. Reads the same
 * arrival-dated request rows the seasonality panel uses.
 */
export function BookingForecastPanel({ rows }: { rows: ForecastInputRow[] }) {
  const f = useMemo(() => buildBookingForecast(rows, 6), [rows]);
  const up = f.trend >= 1;
  const TrendIcon = up ? TrendingUp : TrendingDown;

  return (
    <section
      aria-label="Booking forecast"
      className="mt-8 rounded-2xl border border-border bg-card/60 p-6"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-lg font-semibold text-foreground">Booking forecast</h2>
        <p className="text-xs text-muted-foreground">
          Next 6 months, projected from the last 12 months of arrivals
        </p>
      </div>

      {f.historyMonths === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground">
          Not enough arrival history yet — the forecast appears once stays have been booked.
        </p>
      ) : (
        <>
          <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Card
              icon={CalendarDays}
              label="Projected requests"
              value={String(f.totalPredicted)}
              hint={`${f.totalOnBooks} already on the books`}
            />
            <Card
              icon={TrendIcon}
              label="Year-over-year trend"
              value={`${up ? "+" : ""}${Math.round((f.trend - 1) * 100)}%`}
              hint="Last 12 months vs the 12 before"
            />
            <Card
              icon={Users}
              label="Forecast confidence"
              value={f.confidence === "high" ? "High" : f.confidence === "medium" ? "Medium" : "Low"}
              hint={`${f.historyMonths} of the last 12 months have arrivals`}
            />
          </div>

          <div className="mt-6 flex h-32 items-end gap-2">
            {f.months.map((m) => (
              <div key={m.key} className="flex flex-1 flex-col items-center gap-2">
                <div className="relative flex h-full w-full items-end">
                  <div
                    className="w-full rounded-t border border-dashed border-[var(--color-ember)]/70 bg-[var(--color-ember)]/25"
                    style={{
                      height: `${Math.round((m.predictedRequests / f.max) * 100)}%`,
                      minHeight: 4,
                    }}
                    title={`${m.label}: ~${m.predictedRequests} requests projected, ${m.onBooks} booked`}
                    aria-label={`${m.label}: about ${m.predictedRequests} requests projected, ${m.onBooks} already booked`}
                  >
                    <div
                      className="absolute bottom-0 left-0 w-full rounded-t bg-[var(--color-ember)]/80"
                      style={{
                        height: `${Math.round((m.onBooks / f.max) * 100)}%`,
                        minHeight: m.onBooks ? 3 : 0,
                      }}
                    />
                  </div>
                </div>
                <span className="whitespace-nowrap text-[0.6rem] text-muted-foreground">{m.label}</span>
              </div>
            ))}
          </div>

          <div className="mt-6 overflow-x-auto">
            <table className="w-full min-w-[34rem] text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="py-2 pr-4 font-medium">Month</th>
                  <th className="py-2 pr-4 font-medium">Projected requests</th>
                  <th className="py-2 pr-4 font-medium">Already booked</th>
                  <th className="py-2 pr-4 font-medium">Projected approved</th>
                  <th className="py-2 pr-4 font-medium">Projected guest nights</th>
                  <th className="py-2 font-medium">Last year</th>
                </tr>
              </thead>
              <tbody>
                {f.months.map((m) => (
                  <tr key={m.key} className="border-t border-border/60">
                    <td className="py-2 pr-4 text-foreground">{m.label}</td>
                    <td className="py-2 pr-4 text-foreground">~{m.predictedRequests}</td>
                    <td className="py-2 pr-4 text-muted-foreground">{m.onBooks}</td>
                    <td className="py-2 pr-4 text-muted-foreground">~{m.predictedApproved}</td>
                    <td className="py-2 pr-4 text-muted-foreground">~{m.predictedNights}</td>
                    <td className="py-2 text-muted-foreground">
                      {m.basis === "last-year" ? m.lastYearRequests : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="mt-4 text-xs text-muted-foreground">
            Estimates only. Each month starts from the same month last year, adjusted for the
            year-over-year trend; months with no history use the 12-month average. Solid bars are
            requests already received, outlined bars are the projection.
          </p>
        </>
      )}
    </section>
  );
}

function Card({
  icon: Icon,
  label,
  value,
  hint,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  hint: string;
}) {
  return (
    <div className="rounded-xl border border-border bg-background/40 p-4">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
        <Icon className="h-4 w-4" />
        {label}
      </div>
      <p className="mt-2 text-2xl font-semibold text-foreground">{value}</p>
      <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
    </div>
  );
}
